import { useState } from "react";

import type {
  DemoSplitRule,
  DemoSplitVariant,
  DemoWallSplitVariants,
  SplitPriority,
} from "@/api";
import { Button } from "@/components/Button";
import { SelectField } from "@/components/fields/SelectField";
import { formatMm } from "@/lib/format";

import styles from "./SplitVariants.module.css";

/**
 * Fall C on the board (PRD §22.5, design 2026-10-14): what each splitting
 * rule would lay on one belegte wall, side by side, with the evaluation the
 * server worked out — and the Vorgang's priority that decides which of them
 * „Wand belegen" lays.
 *
 * **Describes, never chooses.** Nothing here re-lays a wall by itself: the
 * priority is a prefill (design §3), and the one way to lay another variant
 * is the gesture that says so, „Wand neu aufteilen". A free choice per wall is
 * the next stage (design §5); it comes in as an action on {@link VariantRow}
 * and needs nothing on this list to change shape.
 */

/** A figure without its unit — the German grouping stays in `formatMm`. */
function plain(valueMm: number): string {
  return formatMm(valueMm).replace(/\s*mm$/, "");
}

/**
 * `[1000, 1000, 420]` → `2 x 1.000 + 420 mm`.
 *
 * The elements in laying order, a run of equal lengths counted once — order
 * kept, because „1.000 + 1.420" and „1.420 + 1.000" are two different walls.
 * The quantity sign is the `x` the parts list and the server's sentences use.
 */
export function variantFigure(lengthsMm: readonly number[]): string {
  const runs: { lengthMm: number; count: number }[] = [];
  for (const lengthMm of lengthsMm) {
    const last = runs.at(-1);
    if (last && last.lengthMm === lengthMm) last.count += 1;
    else runs.push({ lengthMm, count: 1 });
  }
  const parts = runs.map((run) =>
    run.count > 1 ? `${run.count} x ${plain(run.lengthMm)}` : plain(run.lengthMm),
  );
  return `${parts.join(" + ")} mm`;
}

/**
 * The server's evaluation without what the figure line above it already says.
 *
 * `planning/variants.py::_summary` opens with the elements spelled out —
 * „10 Elemente: 9 x 1.000 mm, Sonderlänge 1.360 mm · 46 Konsolen …" — right
 * under „9 x 1.000 + 1.360 mm". The row keeps the count and the rest
 * (Generalprobe 15.10.2026). A sentence of another shape stays as it came:
 * the server's words, shortened only where the shortening is certain.
 */
export function evaluationAfterFigure(summary: string): string {
  const [first = "", ...rest] = summary.split(" · ");
  const count = /^(\d+ Elemente?): /.exec(first);
  if (count === null || rest.length === 0) return summary;
  return [count[1], ...rest].join(" · ");
}

/**
 * Whether the Vorgang's priority lays this variant.
 *
 * The server sends the same flag (`preferred`, which is `priority in rules`
 * in `planning/variants.py`) — read here off the store's priority instead,
 * so the mark and the select beside it never disagree in the moment between
 * a change and the answer to it, nor after an answer that failed.
 */
export function prefers(variant: DemoSplitVariant, priority: SplitPriority): boolean {
  return variant.rules.includes(priority);
}

/** Whether a wall lies as the priority would lay it. */
export function liesAsPreferred(
  wall: DemoWallSplitVariants,
  priority: SplitPriority,
): boolean {
  return wall.variants.some((variant) => variant.current && prefers(variant, priority));
}

/** One variant: its rule, its marks, its elements, its evaluation. */
function VariantRow({ variant, preferred }: { variant: DemoSplitVariant; preferred: boolean }) {
  return (
    <li
      className={styles.row}
      data-current={variant.current}
      data-preferred={preferred}
      data-role="split-variant"
    >
      <span className={styles.head}>
        <span className={styles.name}>{variant.label}</span>
        {/* Words, not a colour: „liegt jetzt" and „Vorbelegung" are read
            out with the row and stand in print. */}
        {variant.current ? (
          <span className={`label ${styles.mark} ${styles.markNow}`}>liegt jetzt</span>
        ) : null}
        {preferred ? <span className={`label ${styles.mark}`}>Vorbelegung</span> : null}
      </span>
      <span className={`num ${styles.figure}`}>
        {variantFigure(variant.elements.map((each) => each.lengthMm))}
      </span>
      <span className={styles.summary}>{evaluationAfterFigure(variant.summary)}</span>
    </li>
  );
}

/**
 * The variants of one wall in the server's order — the rules' order, never
 * re-sorted: a list sorted by consoles would be a recommendation nobody made.
 *
 * Mount it with `key={wallIndex}`: the live region below then says a changed
 * prefill on *this* wall, and stays quiet when somebody picks the next one.
 */
export function SplitVariantList({
  wall,
  priority,
}: {
  wall: DemoWallSplitVariants;
  priority: SplitPriority;
}) {
  const preferred = wall.variants.find((variant) => prefers(variant, priority)) ?? null;

  // Said once when the prefill moves, never on the first render: the row's
  // mark moves without a sound otherwise, and „wirkt beim Belegen" means
  // nothing visible happens on the board either.
  // React's „state from the previous render": compared during render, so the
  // sentence lands with the render that moved the mark, not one after it.
  // The sentence goes again once the wall comes to lie as marked („Wand neu
  // aufteilen"): left standing, a screen reader's reading mode would still
  // find „liegt noch nicht so" under a wall that does.
  const label = preferred?.label ?? null;
  const lies = preferred?.current ?? false;
  const [seen, setSeen] = useState({ label, lies });
  const [said, setSaid] = useState("");
  if (label !== seen.label || lies !== seen.lies) {
    setSeen({ label, lies });
    if (label !== seen.label && preferred !== null) {
      setSaid(
        `Vorbelegt ist jetzt „${preferred.label}“. Wand ${wall.wallIndex + 1} liegt ${
          lies ? "schon" : "noch nicht"
        } so.`,
      );
    } else if (lies) {
      setSaid("");
    }
  }

  return (
    <div className={styles.block}>
      <p className={`label ${styles.listHead}`}>
        {wall.variants.length === 1 ? "Aufteilung" : "Aufteilungen"}{" "}
        <span className="num">{wall.variants.length}</span>
      </p>
      <ul className={styles.list} aria-label={`Aufteilungen für Wand ${wall.wallIndex + 1}`}>
        {wall.variants.map((variant) => (
          <VariantRow
            key={variant.rules.join("+")}
            variant={variant}
            preferred={prefers(variant, priority)}
          />
        ))}
      </ul>
      {wall.variants.length === 1 ? (
        <span className={styles.hint}>Jede Regel legt hier dieselben Elemente.</span>
      ) : null}
      <p role="status" className="visually-hidden">
        {said}
      </p>
    </div>
  );
}

/**
 * The offer under the list where the wall does not lie as the priority would
 * lay it — and nothing at all where it does. The button is the one gesture
 * that lays a variant (`cover(wall, true)` on the screen).
 */
export function RelayOffer({
  wall,
  priority,
  busy,
  disabled = busy,
  onRelay,
}: {
  wall: DemoWallSplitVariants;
  priority: SplitPriority;
  /** This button's own call is under way. */
  busy: boolean;
  /** Any call on this wall is — „Wand belegen" beside it waits on the same one. */
  disabled?: boolean;
  onRelay: () => void;
}) {
  if (liesAsPreferred(wall, priority)) return null;
  const preferred = wall.variants.find((variant) => prefers(variant, priority)) ?? null;
  if (preferred === null) return null;
  const current = wall.variants.find((variant) => variant.current) ?? null;

  return (
    <div className={styles.offer} data-role="relay-offer">
      <span className={styles.offerText}>
        {current
          ? `Liegt nach „${current.label}“, vorbelegt ist „${preferred.label}“.`
          : `Die Roste dieser Wand folgen keiner dieser Aufteilungen. Vorbelegt ist „${preferred.label}“.`}
      </span>
      <Button variant="secondary" onClick={onRelay} disabled={disabled} busy={busy}>
        {busy ? `Wand ${wall.wallIndex + 1} wird neu aufgeteilt …` : "Wand neu aufteilen"}
      </Button>
      <span className={styles.hint}>
        Ersetzt die Roste dieser Wand, auch von Hand getippte Maße. Rückgängig holt sie zurück.
      </span>
    </div>
  );
}

/**
 * „Aufteilung" — the Vorgang's priority, from the catalogue the answer carries
 * (`splitRules`). Nothing until the catalogue is there: a select offering raw
 * rule keys would state a choice in words nobody uses.
 *
 * The hint says what the setting does *not* do, because that is the surprise:
 * changing it moves no grating.
 */
export function SplitPriorityField({
  id,
  rules,
  value,
  status,
  onChange,
}: {
  id: string;
  rules: readonly DemoSplitRule[];
  value: SplitPriority;
  /**
   * A sentence under the field, in a live region that stands as long as the
   * field does — so it is read out when it changes. `undefined`: no region.
   * Empty takes no room (`.status:empty`).
   */
  status?: string;
  onChange: (priority: SplitPriority) => void;
}) {
  if (rules.length === 0) return null;
  const rule = rules.find((each) => each.priority === value) ?? null;
  return (
    <div className={styles.setting}>
      <SelectField
        id={id}
        label="Aufteilung"
        value={value}
        options={rules.map((each) => ({ value: each.priority, label: each.label }))}
        hint={`${rule ? `${rule.description} ` : ""}Wirkt beim Belegen. Was schon liegt, bleibt liegen, bis du die Wand neu aufteilst.`}
        onChange={(next) => {
          const picked = rules.find((each) => each.priority === next);
          if (picked) onChange(picked.priority);
        }}
      />
      {status === undefined ? null : (
        <p role="status" className={styles.status} data-role="split-mismatch">
          {status}
        </p>
      )}
    </div>
  );
}

/**
 * The walls that do not lie as the priority would lay them, as one sentence —
 * or `null`. For the step's own panel, where no wall is picked and a changed
 * priority would otherwise change nothing anyone can see.
 */
export function mismatchSentence(
  walls: readonly DemoWallSplitVariants[],
  priority: SplitPriority,
  ruleLabel: string,
): string | null {
  const off = walls
    .filter((wall) => !liesAsPreferred(wall, priority))
    .map((wall) => `Wand ${wall.wallIndex + 1}`);
  if (off.length === 0) return null;
  // A list of walls is read out loud — „Wand 1, Wand 3 und Wand 4", the way
  // the screen's own „tragen kein Maß" names them.
  const named =
    off.length === 1
      ? `${off[0]} liegt`
      : `${off.slice(0, -1).join(", ")} und ${off.at(-1)} liegen`;
  return `${named} anders, als „${ruleLabel}“ legen würde. Zum Vergleichen die Wand anklicken.`;
}
